// Intercept fetch API for Shopify cart requests.

const { fetch: origFetch } = window

window.fetch = async (...args) => {
  args = transformNativeBundleSubscriptionCartAdd(args)

  const response = await origFetch(...args)

  const url = args[0].toString();

  const clonedResp = response.clone()

  if (url.indexOf('/cart.js') !== -1) {
    const result = await clonedResp.json()
    document.dispatchEvent(new CustomEvent('on:cart:loaded', { detail: { cart: result } }))
  }

  if (url.indexOf('/cart/add.js') !== -1) {
    const result = await clonedResp.json()
    document.dispatchEvent(new CustomEvent('on:cart:add', { detail: { cart: result } }))
  }

  if (url.indexOf('/cart/update.js') !== -1 || url.indexOf('/cart/change.js') !== -1) {
    const result = await clonedResp.json()
    document.dispatchEvent(new CustomEvent('on:cart:update', { detail: { cart: result } }))
  }

  if (url.indexOf('/cart/clear.js') !== -1) {
    const result = await clonedResp.json()
    document.dispatchEvent(new CustomEvent('on:cart:clear', { detail: { cart: result } }))
  }

  return response
}

function transformNativeBundleSubscriptionCartAdd(args) {
  const url = args[0]?.toString?.() || '';
  const options = args[1];

  if (url.indexOf('/cart/add.js') === -1 || !options || !options.body) return args;

  const bundleDataList = getNativeBundleCartDataList();
  if (bundleDataList.length === 0) return args;

  const parsedRequest = parseCartAddRequestBody(options.body);
  if (!parsedRequest) return args;

  const transformedItems = [];
  let transformed = false;

  parsedRequest.items.forEach((item) => {
    const sellingPlanId = getItemSellingPlanId(item);
    const bundleData = bundleDataList.find((data) => isBundleParentItem(item, data));

    if (!sellingPlanId || !bundleData) {
      transformedItems.push(item);
      return;
    }

    const componentItems = buildBundleComponentItems(bundleData, item, sellingPlanId);

    if (!componentItems || componentItems.length === 0) {
      transformedItems.push(item);
      return;
    }

    transformed = true;
    transformedItems.push(...componentItems);
  });

  if (!transformed) return args;

  const headers = normalizeCartAddJsonHeaders(options.headers);
  const transformedBody = JSON.stringify({ items: transformedItems });

  console.groupCollapsed('[TD Bundle Cart] transformed subscription bundle add');
  console.log({
    originalBody: options.body,
    transformedBody,
    bundleDataList
  });
  console.groupEnd();

  return [
    args[0],
    {
      ...options,
      headers,
      body: transformedBody
    },
    ...args.slice(2)
  ];
}

function getNativeBundleCartDataList() {
  const dataScripts = document.querySelectorAll('[data-td-bundle-component-cart-data]');
  if (dataScripts.length === 0) return [];

  return Array.from(dataScripts).map((dataScript) => {
    try {
      return JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('[TD Bundle Cart] Unable to parse bundle component cart data.', error);
      return null;
    }
  }).filter((bundleData) => (
    bundleData &&
    Array.isArray(bundleData.components) &&
    bundleData.components.length > 0
  ));
}

function parseCartAddRequestBody(body) {
  if (typeof body !== 'string') return null;

  try {
    const parsedJson = JSON.parse(body);
    if (parsedJson?.items && Array.isArray(parsedJson.items)) {
      return { items: parsedJson.items };
    }
    if (parsedJson?.id) {
      return { items: [parsedJson] };
    }
  } catch (error) {
    // Fall through to URL-encoded form parsing.
  }

  const params = new URLSearchParams(body);
  const id = params.get('id');
  if (!id) return null;

  const properties = {};
  params.forEach((value, key) => {
    const propertyMatch = key.match(/^properties\[(.+)\]$/);
    if (propertyMatch) {
      properties[propertyMatch[1]] = value;
    }
  });

  return {
    items: [
      {
        id,
        quantity: params.get('quantity') || '1',
        selling_plan: params.get('selling_plan') || '',
        properties
      }
    ]
  };
}

function normalizeCartAddJsonHeaders(headers) {
  const nextHeaders = new Headers(headers || {});
  nextHeaders.set('Content-Type', 'application/json');
  nextHeaders.set('Accept', 'application/json');
  return nextHeaders;
}

function isBundleParentItem(item, bundleData) {
  const itemVariantId = String(item.id || item.variant_id || '');
  const itemProductId = String(item['product-id'] || item.product_id || '');

  return (
    itemVariantId === String(bundleData.variantId || '') ||
    itemProductId === String(bundleData.productId || '')
  );
}

function getItemSellingPlanId(item) {
  return String(item.selling_plan || item.sellingPlan || '').trim();
}

function buildBundleComponentItems(bundleData, sourceItem, sellingPlanId) {
  const selectedParentPlanIndex = getSellingPlanIndex(bundleData.sellingPlans, sellingPlanId);
  const selectedParentPlan = bundleData.sellingPlans?.[selectedParentPlanIndex] || null;
  const sourceQuantity = Number(sourceItem.quantity) || 1;
  const sourceProperties = getSourceItemProperties(sourceItem);

  const componentItems = [];

  for (const component of bundleData.components) {
    const componentSellingPlan = findMatchingComponentSellingPlan(
      component.sellingPlans || [],
      sellingPlanId,
      selectedParentPlan,
      selectedParentPlanIndex
    );

    if (!componentSellingPlan?.id) {
      console.error('[TD Bundle Cart] Missing component selling plan for bundle subscription item.', {
        component,
        sellingPlanId,
        selectedParentPlan
      });
      return null;
    }

    componentItems.push({
      id: component.variantId,
      quantity: (Number(component.quantity) || 1) * sourceQuantity,
      selling_plan: componentSellingPlan.id,
      properties: sourceProperties
    });
  }

  return componentItems;
}

function getSourceItemProperties(item) {
  const properties = {
    ...(item.properties || {})
  };

  Object.keys(item).forEach((key) => {
    const propertyMatch = key.match(/^properties\[(.+)\]$/);
    if (propertyMatch) {
      properties[propertyMatch[1]] = item[key];
    }
  });

  return properties;
}

function getSellingPlanIndex(sellingPlans = [], sellingPlanId) {
  const index = sellingPlans.findIndex((sellingPlan) => String(sellingPlan.id) === String(sellingPlanId));
  return index > -1 ? index : 0;
}

function findMatchingComponentSellingPlan(componentPlans, sellingPlanId, selectedParentPlan, selectedParentPlanIndex) {
  return (
    componentPlans.find((sellingPlan) => String(sellingPlan.id) === String(sellingPlanId)) ||
    componentPlans.find((sellingPlan) => areSellingPlansEquivalent(sellingPlan, selectedParentPlan)) ||
    componentPlans[selectedParentPlanIndex] ||
    null
  );
}

function areSellingPlansEquivalent(planA, planB) {
  if (!planA || !planB) return false;

  return (
    normalizeSellingPlanName(planA.name) === normalizeSellingPlanName(planB.name) ||
    normalizeSellingPlanOptions(planA.options) === normalizeSellingPlanOptions(planB.options)
  );
}

function normalizeSellingPlanName(name) {
  return String(name || '').trim().toLowerCase();
}

function normalizeSellingPlanOptions(options) {
  if (!Array.isArray(options)) return '';

  return options.map((option) => {
    if (typeof option === 'string') return option;
    return option.value || option.name || '';
  }).join('|').trim().toLowerCase();
}
